<Container shake={shakeConfig} freeze={mapFrozen}>
  @if (backgroundAmbientSound()) {
    <Container sound={backgroundAmbientSound()} />
  }

  <Container>
    @if (map() && sceneComponent() && sceneData() != null) {
      <sceneComponent() data={renderedSceneData()} params={map()?.params ?? emptySceneParams} />
    }
  </Container>

  @for (child of children) {
    <child />
  }

  @for (componentAnimation of componentAnimations) {
    <Container>
      @for (animation of componentAnimation.current) {
        <componentAnimation.component ...animation />
      }
    </Container>
  }

  <Container sortableChildren={true}>
    @for (projectile of projectiles() ; track projectile.id) {
      <projectile.component ...projectile.props />
    }
  </Container>

  @for (builtinWeather of builtinWeatherList() ; track builtinWeather.key) {
    <Weather ...builtinWeather.props />
  }
  @for (customWeather of customWeatherList() ; track customWeather.key) {
    <Container zIndex={customWeather.zIndex}>
      <customWeather.component ...customWeather.props />
    </Container>
  }
</Container>

<script>
    import { computed, effect } from 'canvasengine'
    import './weather-lifecycle-compat'
    import { inject } from "../../core/inject";
    import { RpgClientEngine } from "../../RpgClientEngine";
    import { Weather, getWeatherPreset } from '@canvasengine/presets'
    import { resolveWeatherRender } from './weather-render'

    const { children } = defineProps()
    const engine = inject(RpgClientEngine);
    const componentAnimations = engine.componentAnimations
    // Stable list whose items carry reactive props: components are not rebuilt every frame
    const projectiles = engine.projectiles.renderList
    const map = engine.sceneMap?.data
    const emptySceneParams = {}
    const sceneComponent = computed(() => map()?.component)
    const sceneData = computed(() => map()?.data)
    // During a map transfer the data is cleared before the @if unmounts the
    // scene component; keep feeding it the last data so it never reads undefined.
    let lastSceneData
    const renderedSceneData = computed(() => {
      const data = sceneData()
      if (data != null) lastSceneData = data
      return lastSceneData
    })
    const mapFrozen = computed(() => engine.gamePause() || engine.visualPause())
    const weather = engine.sceneMap.weather
    effect(() => engine.music.setMap(map()?.params?.backgroundMusic))
    const backgroundAmbientSound = computed(() => {
      const src = map()?.params?.backgroundAmbientSound
      return src ? { src, autoplay: true, loop: true, volume: engine.getSoundVolume("sfx") } : undefined
    })

    const shakeConfig = {
      trigger: engine.mapShakeTrigger,
      intensity: 10,
      duration: 500,
      frequency: 10,
      direction: 'both'
    }

    const weatherRender = computed(() => resolveWeatherRender(weather?.(), {
      customIds: engine.weatherComponents,
      hasPreset: (name) => getWeatherPreset(name) !== undefined,
    }))
    // CanvasEngine reads `preset` and `effect` when <Weather> is created: keying the list on them
    // rebuilds it when the weather switches to another preset or effect.
    const builtinWeatherList = computed(() => {
      const render = weatherRender()
      if (!render || render.kind !== 'builtin') return []
      return [{ key: `${render.props.effect ?? ''}|${render.props.preset ?? ''}`, props: render.props }]
    })
    const customWeatherList = computed(() => {
      const render = weatherRender()
      const component = render && render.kind === 'custom' ? engine.weatherComponents.get(render.id) : null
      if (!render || render.kind !== 'custom' || !component) return []
      return [{ key: render.id, component, props: render.props, zIndex: render.props.zIndex }]
    })
</script>
